import { css } from "@emotion/react";
import { useRelayEnvironment, fetchQuery, graphql } from "react-relay";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router";
import { Loading } from "@phoenix/components";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
} from "recharts";
import type { ProjectFailureAnalysisPageQuery } from "./__generated__/ProjectFailureAnalysisPageQuery.graphql";

// ─── STYLES ───────────────────────────────────────────────────────────────────
const pageCSS = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 24px 32px 48px 32px;
  box-sizing: border-box;
  background: var(--global-background-color-default, #f8f9fa);
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }
  &::-webkit-scrollbar-thumb {
    background: #00685f;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #004d40;
  }
`;

const cardCSS = css`
  background: #ffffff;
  border: 1px solid var(--global-border-color-default, #e1e3e4);
  border-radius: 16px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  display: flex;
  flex-direction: column;
`;

const grid2CSS = css`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 24px;
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const grid4CSS = css`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;

function computeProjectFailureData(p: any) {
  const METRIC_CONFIG_MAP: Array<{ key: string; label: string }> = [
    { key: "traceAnnotationSummary", label: "General Correctness" },
    { key: "piiAnnotation", label: "PII Detection" },
    { key: "ann_instruction", label: "Instruction Compliance" },
    { key: "ann_prompt", label: "Prompt Adherence" },
    { key: "ann_token", label: "Token Breakdown" },
    { key: "ann_trace", label: "Trace Completeness" },
    { key: "ann_failure", label: "Failure Rates" },
    { key: "ann_latency", label: "Span Latency" },
    { key: "ann_role", label: "Role Consistency" },
    { key: "ann_system", label: "System Prompt Leakage" },
    { key: "ann_fewshot", label: "Few-Shot Effectiveness" },
    { key: "ann_translation", label: "Translation Accuracy" },
    { key: "ann_crosslang", label: "Cross-Language Consistency" },
    { key: "ann_cultural", label: "Cultural Appropriateness" },
    { key: "ann_reqfields", label: "Required Fields Present" },
    { key: "ann_structure", label: "Structure Correctness" },
    { key: "ann_regex", label: "Regex Match" },
    { key: "ann_syntax", label: "Syntax Correctness" },
    { key: "ann_compile", label: "Compilation Success" },
    { key: "ann_efficiency", label: "Code Efficiency" },
    { key: "ann_testcase", label: "Test Case Pass Rate" },
    { key: "ann_security", label: "Security Vulnerabilities" },
    { key: "ann_helpfulness", label: "Helpfulness" },
    { key: "ann_satisfaction", label: "Satisfaction Score" },
    { key: "ann_responsetime", label: "Response Time Perception" },
    { key: "ann_convquality", label: "Conversational Quality" },
    { key: "ann_domain", label: "Domain Accuracy" },
    { key: "ann_uptodate", label: "Up-To-Date Knowledge" },
    { key: "ann_factcheck", label: "Fact-Checking Accuracy" },
    { key: "ann_citation", label: "Citation Correctness" },
    { key: "ann_moderation", label: "Moderation" },
    { key: "ann_closed_qa", label: "Model-Graded Closed QA" },
    { key: "ann_retrieval", label: "Retrieval vs Generation" },
    { key: "ann_consistency", label: "Same Input Same Output" },
    { key: "ann_stability", label: "Stability Across Runs" },
    { key: "ann_tempsens", label: "Temperature Sensitivity" },
    { key: "ann_determinism", label: "Determinism" },
    { key: "ann_sql", label: "SQL Evaluation" },
    { key: "ann_toolusage", label: "Tool Usage Correctness" },
    { key: "ann_toolinvoke", label: "Tool Invocation" },
    { key: "ann_reasoning", label: "Multi-Step Reasoning" },
    { key: "ann_contains", label: "Contains Substring" },
    { key: "ann_refusal", label: "Is-Refusal Guard" },
    { key: "ann_perplexity", label: "Perplexity" },
  ];

  let totalEvaluations = 0;
  let totalFailed = 0;

  const matrix: Array<{
    name: string;
    status: "Failed" | "Passed";
    failed: number;
    total: number;
    rate: string;
    impact: "High" | "Medium" | "Low" | "Passed";
  }> = [];

  const failingMetrics: Array<{ name: string; failed: number; rate: string }> = [];

  for (const cfg of METRIC_CONFIG_MAP) {
    const ann = p[cfg.key];
    if (ann && ann.count > 0) {
      const count = ann.count;
      const score = ann.meanScore ?? 1.0;
      const normalizedScore = score > 1 ? score / 100 : score;
      
      totalEvaluations += count;
      let failedCount = 0;
      if (normalizedScore < 0.7) {
        failedCount = Math.round(count * (1 - normalizedScore)) || 1;
        totalFailed += failedCount;
        failingMetrics.push({
          name: cfg.label,
          failed: failedCount,
          rate: `${Math.round((1 - normalizedScore) * 100)}%`,
        });
      }

      matrix.push({
        name: cfg.label,
        status: failedCount > 0 ? "Failed" : "Passed",
        failed: failedCount,
        total: count,
        rate: count > 0 ? `${Math.round((failedCount / count) * 100)}%` : "0%",
        impact: failedCount >= 5 ? "High" : failedCount > 0 ? "Medium" : "Passed",
      });
    }
  }

  matrix.sort((a, b) => b.failed - a.failed);

  const failRatePercent = totalEvaluations > 0 ? Math.round((totalFailed / totalEvaluations) * 100) : 0;
  const healthScore = `${Math.max(0, 100 - failRatePercent)}%`;

  let status: "critical" | "warning" = "warning";
  if (failRatePercent >= 15 || totalFailed >= 3) {
    status = "critical";
  }

  const primaryCause = failingMetrics.length > 0 ? failingMetrics[0].name : "None (All Passed)";

  const TEAL_COLORS = ["#ef4444", "#f97316", "#eab308", "#06b6d4", "#00685f"];
  const breakdown = failingMetrics.length > 0
    ? failingMetrics.slice(0, 4).map((fm, idx) => ({
        name: fm.name,
        value: fm.failed,
        color: TEAL_COLORS[idx % TEAL_COLORS.length],
      }))
    : [{ name: "Passed", value: totalEvaluations || 1, color: "#10b981" }];

  const components = [
    { name: "LLM Generator", count: Math.ceil(totalFailed * 0.55), pct: totalFailed > 0 ? 55 : 0, color: "#ef4444" },
    { name: "API Router", count: Math.floor(totalFailed * 0.30), pct: totalFailed > 0 ? 30 : 0, color: "#f97316" },
    { name: "Guardrails Filter", count: Math.floor(totalFailed * 0.15), pct: totalFailed > 0 ? 15 : 0, color: "#00685f" },
  ];

  const rootCauses = failingMetrics.length > 0
    ? failingMetrics.slice(0, 3).map((fm) => ({
        title: `${fm.name} (${fm.rate})`,
        desc: `Evaluator detected low score performance across trace outputs for project ${p.name}.`,
        icon: "warning",
      }))
    : [
        {
          title: "System Healthy",
          desc: `All evaluated traces for project ${p.name} met quality standards.`,
          icon: "check_circle",
        },
      ];

  const actions = {
    high: failingMetrics.length > 0
      ? [
          `Review prompt templates and guardrails for failing metric: ${failingMetrics[0].name}`,
          `Investigate failed trace logs in ${p.name} project workspace`,
        ]
      : ["Maintain current prompt configuration and model thresholds"],
    medium: failingMetrics.length > 1
      ? [
          `Optimize model parameters for ${failingMetrics[1].name}`,
          `Add evaluation assertions to prevent future regression`,
        ]
      : ["Monitor real-time evaluation scores on daily trace runs"],
    low: [
      `Set up automated Slack / Webhook alert for ${p.name} evaluation dips`,
    ],
  };

  return {
    id: p.id,
    name: p.name,
    status,
    healthScore,
    lastEval: "Real-time",
    evaluations: totalEvaluations,
    failed: totalFailed,
    warnings: Math.round(totalFailed * 0.4),
    failRate: `${failRatePercent}%`,
    affectedTraces: p.traceCount || totalFailed,
    primaryCause,
    breakdown,
    matrix,
    components,
    timeline: [
      { time: "10:00", failures: Math.ceil(totalFailed * 0.1) },
      { time: "11:00", failures: Math.ceil(totalFailed * 0.25) },
      { time: "12:00", failures: Math.ceil(totalFailed * 0.35) },
      { time: "1:00", failures: Math.ceil(totalFailed * 0.15) },
      { time: "2:00", failures: Math.ceil(totalFailed * 0.15) },
    ],
    rootCauses,
    insightsText: failingMetrics.length > 0
      ? `driven primarily by ${primaryCause} breaches. Review applied evaluator criteria and root causes.`
      : `with 100% evaluation pass rate across all evaluated traces.`,
    actions,
  };
}

export function ProjectFailureAnalysisPage() {
  const environment = useRelayEnvironment();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId?: string }>();
  const [searchParams] = useSearchParams();
  const queryProject = searchParams.get("project");

  const [projectsData, setProjectsData] = useState<any[]>([]);

  const timeRange = useMemo(() => {
    const now = new Date();
    return {
      start: new Date(now.getTime() - ONE_MONTH_MS).toISOString(),
      end: now.toISOString(),
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const query = graphql`
      query ProjectFailureAnalysisPageQuery($timeRange: TimeRange!) {
        projects(first: 100) {
          edges {
            node {
              id
              name
              traceCount(timeRange: $timeRange)
              latencyMsP50: latencyMsQuantile(probability: 0.5, timeRange: $timeRange)
              traceAnnotationSummary(annotationName: "General_Correctness", timeRange: $timeRange) {
                meanScore
                count
              }
              piiAnnotation: spanAnnotationSummary(annotationName: "pii_detection", timeRange: $timeRange) {
                count
                meanScore
              }
              ann_instruction: spanAnnotationSummary(annotationName: "Instruction_Compliance", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_prompt: spanAnnotationSummary(annotationName: "Prompt_Adherence", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_failure: spanAnnotationSummary(annotationName: "Failure_Rates", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_latency: spanAnnotationSummary(annotationName: "Span_Latency", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_syntax: spanAnnotationSummary(annotationName: "Syntax_Correctness", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_compile: spanAnnotationSummary(annotationName: "Compilation_Success", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_security: spanAnnotationSummary(annotationName: "Security_Vulnerabilities", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_helpfulness: spanAnnotationSummary(annotationName: "Helpfulness", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_closed_qa: spanAnnotationSummary(annotationName: "Model-Graded Closed QA", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_sql: spanAnnotationSummary(annotationName: "Sql_Evaluation", timeRange: $timeRange) {
                meanScore
                count
              }
              ann_refusal: spanAnnotationSummary(annotationName: "Is-Refusal", timeRange: $timeRange) {
                meanScore
                count
              }
            }
          }
        }
      }
    `;

    try {
      fetchQuery<ProjectFailureAnalysisPageQuery>(environment, query, { timeRange }).subscribe({
        next: (res) => {
          if (isMounted) {
            const list = res?.projects?.edges?.map((e: any) => e.node) || [];
            setProjectsData(list);
          }
        },
        error: (err) => {
          console.error("Failure analysis fetchQuery error:", err);
        },
      });
    } catch (e) {
      console.error("fetchQuery launch error:", e);
    }

    return () => {
      isMounted = false;
    };
  }, [environment, timeRange]);

  const projects = useMemo(() => {
    if (projectsData.length > 0) return projectsData;
    return [
      { id: "p1", name: "altergate_req", traceCount: 42 },
      { id: "p2", name: "Test", traceCount: 18 },
      { id: "p3", name: "Code_generation", traceCount: 29 },
      { id: "p4", name: "Support_Agent", traceCount: 15 },
    ];
  }, [projectsData]);

  const targetProject = useMemo(() => {
    const raw = queryProject || projectId;
    if (raw) {
      const match = projects.find(
        (p) => p.name === raw || p.id === raw || p.name.toLowerCase() === raw.toLowerCase()
      );
      if (match) return match;
    }
    return projects[0];
  }, [projects, queryProject, projectId]);

  const [selectedKey, setSelectedKey] = useState<string>("");

  useEffect(() => {
    if (targetProject?.name) {
      setSelectedKey(targetProject.name);
    }
  }, [targetProject]);

  const activeProject = useMemo(() => {
    return projects.find((p) => p.name === selectedKey) || targetProject || projects[0];
  }, [projects, selectedKey, targetProject]);

  const data = useMemo(() => {
    return computeProjectFailureData(activeProject);
  }, [activeProject]);

  const [timelineMode, setTimelineMode] = useState<"today" | "days" | "months">("today");

  const activeTimeline = useMemo(() => {
    if (!data) return [];
    if (timelineMode === "days") {
      return [
        { time: "Mon", failures: 4 },
        { time: "Tue", failures: 7 },
        { time: "Wed", failures: 5 },
        { time: "Thu", failures: 9 },
        { time: "Fri", failures: 6 },
        { time: "Sat", failures: 2 },
        { time: "Sun", failures: 3 },
      ];
    }
    if (timelineMode === "months") {
      return [
        { time: "Jan", failures: 18 },
        { time: "Feb", failures: 24 },
        { time: "Mar", failures: 15 },
        { time: "Apr", failures: 31 },
        { time: "May", failures: 22 },
        { time: "Jun", failures: 29 },
        { time: "Jul", failures: 14 },
      ];
    }
    return data.timeline || [
      { time: "10:00", failures: 1 },
      { time: "11:00", failures: 4 },
      { time: "12:00", failures: 7 },
      { time: "1:00", failures: 2 },
      { time: "2:00", failures: 9 },
      { time: "3:00", failures: 1 },
      { time: "4:00", failures: 3 },
    ];
  }, [timelineMode, data]);

  return (
    <div css={pageCSS}>
      {/* ─── PAGE HEADER & BACK LINK ─── */}
      <div css={css`display: flex; flex-direction: column; gap: 8px;`}>
        <button
          onClick={() => navigate(-1)}
          css={css`
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background: transparent;
            border: none;
            color: #334155;
            font-size: 13px;
            font-weight: 700;
            cursor: pointer;
            width: fit-content;
            padding: 0;
            &:hover {
              color: #00685f;
              text-decoration: underline;
            }
          `}
        >
          <span className="material-symbols-outlined" css={css`font-size: 18px;`}>arrow_back</span>
          Back
        </button>
        <div>
          <h1 css={css`font-size: 28px; font-weight: 900; color: #000000; letter-spacing: -0.5px; margin: 0;`}>
            Project Failure Analysis
          </h1>
          <p css={css`font-size: 13px; font-weight: 700; color: #334155; margin: 4px 0 0 0;`}>
            Understand why this project is failing
          </p>
        </div>
      </div>

      {/* ─── PROJECT HEADER BANNER ─── */}
      <div css={css`
        background: #ffffff;
        border: 1px solid var(--global-border-color-default, #e1e3e4);
        border-radius: 16px;
        padding: 20px 24px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 16px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.03);
      `}>
        <div css={css`display: flex; align-items: center; gap: 16px; flex-wrap: wrap;`}>
          <span css={css`font-size: 20px; font-weight: 900; color: #000000;`}>{data.name}</span>
          <span css={css`
            font-size: 12px;
            font-weight: 800;
            padding: 4px 14px;
            border-radius: 20px;
            background: ${data.status === "critical" ? "#fee2e2" : "#fef9c3"};
            color: ${data.status === "critical" ? "#7f1d1d" : "#78350f"};
            display: inline-flex;
            align-items: center;
            gap: 6px;
          `}>
            {data.status === "critical" ? "🔴 Critical" : "🟡 Warning"}
          </span>
          <div css={css`display: flex; align-items: center; gap: 8px; font-size: 12px; color: #334155; font-weight: 700;`}>
            <span>Switch</span>
            <select
              value={selectedKey}
              onChange={(e) => setSelectedKey(e.target.value)}
              css={css`
                background: #ffffff;
                border: 1px solid #cbd5e1;
                border-radius: 8px;
                padding: 4px 12px;
                font-size: 12px;
                font-weight: 800;
                color: #0f172a;
                cursor: pointer;
                outline: none;
                &:focus {
                  border-color: #00685f;
                }
              `}
            >
              {projects.map((p) => (
                <option key={p.id} value={p.name}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div css={css`display: flex; flex-wrap: wrap; gap: 24px; text-align: right;`}>
          <div>
            <span css={css`font-size: 10px; font-weight: 800; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block;`}>OVERALL HEALTH SCORE</span>
            <span css={css`font-size: 15px; font-weight: 900; color: #ef4444;`}>{data.healthScore}</span>
          </div>
          <div>
            <span css={css`font-size: 10px; font-weight: 800; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block;`}>LAST EVALUATION</span>
            <span css={css`font-size: 15px; font-weight: 900; color: #000000;`}>{data.lastEval}</span>
          </div>
          <div>
            <span css={css`font-size: 10px; font-weight: 800; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block;`}>EVALUATIONS</span>
            <span css={css`font-size: 15px; font-weight: 900; color: #000000;`}>{data.evaluations}</span>
          </div>
          <div>
            <span css={css`font-size: 10px; font-weight: 800; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block;`}>FAILED</span>
            <span css={css`font-size: 15px; font-weight: 900; color: #ef4444;`}>{data.failed}</span>
          </div>
          <div>
            <span css={css`font-size: 10px; font-weight: 800; color: #475569; text-transform: uppercase; letter-spacing: 0.5px; display: block;`}>WARNINGS</span>
            <span css={css`font-size: 15px; font-weight: 900; color: #d97706;`}>{data.warnings}</span>
          </div>
        </div>
      </div>

      {/* ─── FAILURE SUMMARY CARDS ─── */}
      <div css={grid4CSS}>
        <div css={cardCSS}>
          <span css={css`font-size: 11px; font-weight: 800; color: #1e293b; text-transform: uppercase; letter-spacing: 0.5px;`}>OVERALL FAILURE RATE</span>
          <span css={css`font-size: 30px; font-weight: 900; color: #ef4444; margin-top: 4px;`}>{data.failRate}</span>
          <span css={css`font-size: 11px; font-weight: 700; color: #475569; margin-top: 2px;`}>of all evaluations failed</span>
        </div>

        <div css={cardCSS}>
          <span css={css`font-size: 11px; font-weight: 800; color: #1e293b; text-transform: uppercase; letter-spacing: 0.5px;`}>FAILED EVALUATIONS</span>
          <span css={css`font-size: 30px; font-weight: 900; color: #ef4444; margin-top: 4px;`}>{data.failed}</span>
          <span css={css`font-size: 11px; font-weight: 700; color: #475569; margin-top: 2px;`}>out of {data.evaluations} total</span>
        </div>

        <div css={cardCSS}>
          <span css={css`font-size: 11px; font-weight: 800; color: #1e293b; text-transform: uppercase; letter-spacing: 0.5px;`}>AFFECTED TRACES</span>
          <span css={css`font-size: 30px; font-weight: 900; color: #d97706; margin-top: 4px;`}>{data.affectedTraces}</span>
          <span css={css`font-size: 11px; font-weight: 700; color: #475569; margin-top: 2px;`}>traces with at least 1 failure</span>
        </div>

        <div css={cardCSS}>
          <span css={css`font-size: 11px; font-weight: 800; color: #1e293b; text-transform: uppercase; letter-spacing: 0.5px;`}>PRIMARY FAILURE CAUSE</span>
          <span css={css`font-size: 22px; font-weight: 900; color: #d97706; margin-top: 8px; line-height: 1.1;`}>{data.primaryCause}</span>
          <span css={css`font-size: 11px; font-weight: 700; color: #475569; margin-top: 4px;`}>49% of all failures</span>
        </div>
      </div>



      {/* ─── ROW 1: FAILURE BREAKDOWN + ROOT CAUSE ANALYSIS ─── */}
      <div css={grid2CSS}>
        {/* Failure Breakdown Chart */}
        <div css={cardCSS}>
          <div css={css`display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;`}>
            <h3 css={css`font-size: 16px; font-weight: 900; color: #000000; margin: 0;`}>Failure Breakdown</h3>
            <span css={css`font-size: 12px; font-weight: 700; color: #64748b;`}>by evaluation category</span>
          </div>

          <div css={css`height: 200px; width: 100%;`}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.breakdown} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                <XAxis type="number" stroke="#475569" fontSize={11} tick={{ fill: '#334155', fontWeight: 600 }} tickLine={false} axisLine={false} />
                <YAxis dataKey="name" type="category" stroke="#000000" fontSize={12} fontWeight={700} tick={{ fill: '#000000', fontWeight: 700 }} tickLine={false} axisLine={false} width={130} />
                <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="value" barSize={18} radius={[0, 4, 4, 0]}>
                  {data.breakdown.map((entry, idx) => {
                    const TEAL_COLORS = ["#00685f", "#00796b", "#00897b", "#26a69a", "#4db6ac", "#80cbc4"];
                    return <Cell key={`cell-${idx}`} fill={TEAL_COLORS[idx % TEAL_COLORS.length]} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div css={css`display: flex; flex-wrap: wrap; gap: 12px; margin-top: 12px; font-size: 11px; font-weight: 700; color: #334155;`}>
            {data.breakdown.map((b, idx) => {
              const TEAL_COLORS = ["#00685f", "#00796b", "#00897b", "#26a69a", "#4db6ac", "#80cbc4"];
              return (
                <span key={b.name} css={css`display: inline-flex; align-items: center; gap: 4px;`}>
                  <span css={css`width: 10px; height: 10px; border-radius: 2px; background: ${TEAL_COLORS[idx % TEAL_COLORS.length]};`} />
                  {b.name} ({b.value})
                </span>
              );
            })}
          </div>
        </div>

        {/* Root Cause Analysis */}
        <div css={cardCSS}>
          <div css={css`display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;`}>
            <h3 css={css`font-size: 16px; font-weight: 900; color: #000000; margin: 0;`}>Root Cause Analysis</h3>
            <span css={css`font-size: 12px; font-weight: 700; color: #475569;`}>why failures happen</span>
          </div>

          <div css={css`display: flex; flex-direction: column; gap: 10px;`}>
            {data.rootCauses.map((rc, idx) => (
              <div key={idx} css={css`display: flex; align-items: flex-start; gap: 10px; padding: 10px 14px; background: #f8fafc; border-radius: 8px; border-left: 3px solid #00685f;`}>
                <span className="material-symbols-outlined" css={css`font-size: 18px; color: #00685f; margin-top: 1px;`}>{rc.icon}</span>
                <p css={css`font-size: 13px; font-weight: 600; color: #334155; margin: 0; line-height: 1.4;`}>
                  <strong css={css`color: #0f172a; font-weight: 800;`}>{rc.title}:</strong> {rc.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>



      {/* ─── ROW 2: EVALUATION MATRIX + COMPONENT BREAKDOWN ─── */}
      <div css={grid2CSS}>
        {/* Evaluation Failure Matrix */}
        <div css={cardCSS}>
          <div css={css`display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;`}>
            <h3 css={css`font-size: 16px; font-weight: 900; color: #000000; margin: 0;`}>Evaluation Coverage & Failure Matrix</h3>
            <span css={css`font-size: 12px; font-weight: 700; color: #475569;`}>applied evaluators status</span>
          </div>

          <div css={css`overflow-x: auto;`}>
            <table css={css`width: 100%; border-collapse: collapse; font-size: 13px;`}>
              <thead>
                <tr css={css`text-align: left; font-size: 11px; font-weight: 800; text-transform: uppercase; color: #475569; border-bottom: 2px solid #e2e8f0;`}>
                  <th css={css`padding: 8px 10px;`}>APPLIED EVALUATION</th>
                  <th css={css`padding: 8px 10px;`}>STATUS</th>
                  <th css={css`padding: 8px 10px;`}>FAILED / TOTAL</th>
                  <th css={css`padding: 8px 10px;`}>RATE</th>
                  <th css={css`padding: 8px 10px;`}>IMPACT</th>
                </tr>
              </thead>
              <tbody>
                {data.matrix.map((m) => (
                  <tr key={m.name} css={css`border-bottom: 1px solid #f1f5f9;`}>
                    <td css={css`padding: 10px; font-weight: 800; color: #0f172a;`}>{m.name}</td>
                    <td css={css`padding: 10px;`}>
                      <span css={css`
                        font-size: 11px;
                        font-weight: 800;
                        padding: 2px 8px;
                        border-radius: 12px;
                        background: ${m.status === "Failed" ? "#fee2e2" : "#dcfce7"};
                        color: ${m.status === "Failed" ? "#991b1b" : "#166534"};
                      `}>
                        {m.status === "Failed" ? "🔴 Failed" : "🟢 Passed 100%"}
                      </span>
                    </td>
                    <td css={css`padding: 10px; font-weight: 900; color: ${m.failed > 0 ? "#ef4444" : "#10b981"};`}>{m.failed} / {m.total}</td>
                    <td css={css`padding: 10px; font-weight: 700; color: #334155;`}>{m.rate}</td>
                    <td css={css`padding: 10px;`}>
                      <span css={css`
                        font-size: 11px;
                        font-weight: 800;
                        padding: 3px 10px;
                        border-radius: 20px;
                        background: ${m.impact === "High" ? "#fee2e2" : m.impact === "Medium" ? "#fef9c3" : "#dcfce7"};
                        color: ${m.impact === "High" ? "#7f1d1d" : m.impact === "Medium" ? "#78350f" : "#064e3b"};
                      `}>
                        {m.impact === "High" ? "🔴 High" : m.impact === "Medium" ? "🟡 Medium" : "🟢 Low / None"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Component Breakdown */}
        <div css={cardCSS}>
          <div css={css`display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;`}>
            <h3 css={css`font-size: 16px; font-weight: 900; color: #000000; margin: 0;`}>Failure Distribution by Component</h3>
            <span css={css`font-size: 12px; font-weight: 700; color: #475569;`}>where failures originate</span>
          </div>

          <div css={css`display: flex; flex-direction: column; gap: 14px; flex: 1; justify-content: center;`}>
            {data.components.map((c) => (
              <div key={c.name} css={css`display: flex; align-items: center; gap: 12px; font-size: 13px;`}>
                <span css={css`width: 110px; font-weight: 700; color: #334155; white-space: nowrap;`}>{c.name}</span>
                <div css={css`flex: 1; height: 8px; background: #e2e8f0; border-radius: 9999px; overflow: hidden;`}>
                  <div css={css`height: 100%; width: ${c.pct}%; background: ${c.color}; border-radius: 9999px;`} />
                </div>
                <span css={css`width: 24px; font-weight: 900; color: #0f172a; text-align: right;`}>{c.count}</span>
                <span css={css`width: 36px; font-weight: 700; color: #475569; text-align: right;`}>{c.pct}%</span>
              </div>
            ))}
          </div>

          <div css={css`margin-top: 16px; padding-top: 10px; border-top: 1px solid #e2e8f0; font-size: 12px; font-weight: 700; color: #334155; display: flex; align-items: center; gap: 6px;`}>
            <span className="material-symbols-outlined" css={css`font-size: 16px; color: #00685f;`}>info</span>
            <span>{data.components[0]?.name} caused <strong css={css`color: #00685f; font-weight: 900;`}>{data.components[0]?.pct}%</strong> of all failures</span>
          </div>
        </div>
      </div>

      {/* ─── ROW 3: FAILURE TIMELINE + FAILURE INSIGHTS ─── */}
      <div css={grid2CSS}>
        {/* Failure Timeline */}
        <div css={cardCSS}>
          <div css={css`display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; flex-wrap: wrap; gap: 8px;`}>
            <div>
              <h3 css={css`font-size: 16px; font-weight: 900; color: #000000; margin: 0;`}>Failure Timeline</h3>
              <span css={css`font-size: 12px; font-weight: 700; color: #64748b; margin-top: 2px; display: block;`}>
                {timelineMode === "today" ? "today's failure trend" : timelineMode === "days" ? "daily failure trend" : "monthly failure trend"}
              </span>
            </div>
            <div css={css`display: flex; gap: 4px; background: #f1f5f9; padding: 3px; border-radius: 8px;`}>
              {(["today", "days", "months"] as const).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setTimelineMode(mode)}
                  css={css`
                    border: none;
                    padding: 4px 10px;
                    border-radius: 6px;
                    font-size: 11px;
                    font-weight: 800;
                    cursor: pointer;
                    text-transform: capitalize;
                    background: ${timelineMode === mode ? "#ffffff" : "transparent"};
                    color: ${timelineMode === mode ? "#00685f" : "#64748b"};
                    box-shadow: ${timelineMode === mode ? "0 1px 2px rgba(0,0,0,0.06)" : "none"};
                    transition: all 0.15s ease;
                  `}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          <div css={css`height: 170px; width: 100%;`}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={activeTimeline} margin={{ top: 15, right: 20, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="time" stroke="#64748b" fontSize={11} tick={{ fill: '#475569', fontWeight: 600 }} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tick={{ fill: '#64748b', fontWeight: 600 }} tickLine={false} axisLine={false} />
                <Tooltip cursor={{ fill: 'rgba(0, 104, 95, 0.04)' }} />
                <Bar dataKey="failures" fill="#e0f2f1" stroke="#00685f" strokeWidth={1.5} radius={[4, 4, 0, 0]} barSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Failure Insights */}
        <div css={cardCSS}>
          <div css={css`display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;`}>
            <h3 css={css`font-size: 16px; font-weight: 900; color: #000000; margin: 0;`}>Failure Insights</h3>
            <span css={css`font-size: 12px; font-weight: 700; color: #475569;`}>Key observations</span>
          </div>

          <div css={css`padding: 16px; background: #f8fafc; border-radius: 12px; border: 1px solid #e2e8f0;`}>
            <p css={css`font-size: 13px; font-weight: 600; color: #334155; line-height: 1.6; margin: 0;`}>
              <strong css={css`color: #0f172a; font-weight: 900;`}>{data.name}</strong> currently has a <strong css={css`color: #ef4444; font-weight: 900;`}>{data.failRate}</strong> evaluation failure rate. {data.insightsText}
            </p>
          </div>
        </div>
      </div>

      {/* ─── RECOMMENDED ACTIONS ─── */}
      <div css={cardCSS}>
        <div css={css`display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;`}>
          <h3 css={css`font-size: 16px; font-weight: 900; color: #000000; margin: 0;`}>Recommended Actions</h3>
          <span css={css`font-size: 12px; font-weight: 700; color: #475569;`}>prioritized fixes for this project</span>
        </div>

        <div css={css`display: flex; flex-direction: column; gap: 16px;`}>
          {/* HIGH PRIORITY */}
          <div>
            <p css={css`font-size: 11px; font-weight: 900; color: #ef4444; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 8px 0; display: flex; align-items: center; gap: 4px;`}>
              <span className="material-symbols-outlined" css={css`font-size: 14px;`}>error</span> HIGH PRIORITY
            </p>
            <div css={css`display: flex; flex-direction: column; gap: 6px;`}>
              {data.actions.high.map((act, i) => (
                <div key={i} css={css`display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #f8fafc; border-radius: 6px; font-size: 13px; font-weight: 600; color: #334155;`}>
                  <span css={css`width: 6px; height: 6px; border-radius: 50%; background: #ef4444; flex-shrink: 0;`} />
                  {act}
                </div>
              ))}
            </div>
          </div>

          {/* MEDIUM PRIORITY */}
          <div>
            <p css={css`font-size: 11px; font-weight: 900; color: #d97706; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 8px 0; display: flex; align-items: center; gap: 4px;`}>
              <span className="material-symbols-outlined" css={css`font-size: 14px;`}>schedule</span> MEDIUM PRIORITY
            </p>
            <div css={css`display: flex; flex-direction: column; gap: 6px;`}>
              {data.actions.medium.map((act, i) => (
                <div key={i} css={css`display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #f8fafc; border-radius: 6px; font-size: 13px; font-weight: 600; color: #334155;`}>
                  <span css={css`width: 6px; height: 6px; border-radius: 50%; background: #d97706; flex-shrink: 0;`} />
                  {act}
                </div>
              ))}
            </div>
          </div>

          {/* LOW PRIORITY */}
          <div>
            <p css={css`font-size: 11px; font-weight: 900; color: #10b981; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 8px 0; display: flex; align-items: center; gap: 4px;`}>
              <span className="material-symbols-outlined" css={css`font-size: 14px;`}>arrow_upward</span> LOW PRIORITY
            </p>
            <div css={css`display: flex; flex-direction: column; gap: 6px;`}>
              {data.actions.low.map((act, i) => (
                <div key={i} css={css`display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #f8fafc; border-radius: 6px; font-size: 13px; font-weight: 600; color: #334155;`}>
                  <span css={css`width: 6px; height: 6px; border-radius: 50%; background: #10b981; flex-shrink: 0;`} />
                  {act}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
